import * as Yup from 'yup';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ROUTING_CONFIG, SESSION_STATES } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';
import { Alert, StickyStackItem, PsychNotesCPTCodesSection, FormikAutoSave, NestedFormik, NestableFormik, CheckboxWithLabel } from '@uplift/ui-v2';
import React, { useState } from 'react';
import { Redirect } from 'react-router';
import { UserSessionInfoCard } from '../../components';
import { useClinicalNotesContext } from './clinical-notes.context';
import { ClinicalNotesHeader, FixedActionBar, SectionContainer } from './components';
import { ScrollToFieldError } from './components/scroll-to-field-error.component';
import { useClinicalNotesForm } from './hooks';
import { AdditionalNotes, Details, DiagnosisSection, MentalStatusExam, Plan, Progress, RiskAssessment, Treatment } from './sections';
import { ClinicalNotesDynamicFormKeyType, ClinicalNotesTemplateType } from './types';
import { useSessionClinicalNoteQuery, useSessionQuery } from '@uplift/queries';
import { patchSession } from '@uplift/api';
import { useQueryClient } from '@tanstack/react-query';
import { PresentingProblems } from './sections/PresentingProblems.component';
import { IdentityVerification } from './sections/IdentityVerification';


const ClinicalNotesCPTCodesSection: React.VFC<{ sessionId: string; paperClassName: string }> = ({ sessionId, paperClassName }) => {
  const l10n = useL10n();
  const queryClient = useQueryClient();
  const { data } = useSessionQuery(sessionId);

  return (
    <NestedFormik
      name="cptCodes"
      enableReinitialize
      validationSchema={Yup.object().shape({
        primaryCptCode: Yup.string().nullable().required(l10n.clinicalNotes.validations.cptCode),
        addonCptCodes: Yup.array().of(Yup.string()).nullable(),
      })}
      initialValues={{
        sessionId: sessionId,
        primaryCptCode: data.cptCodeId,
        addonCptCodes: data.addonCptCodeIds,
      }}
      onSubmit={async (values) => {
        await patchSession(values.sessionId, {
          primaryCptCodeId: values.primaryCptCode,
          addonCptCodeIds: values.addonCptCodes,
        });
        await queryClient.invalidateQueries({ queryKey: [ 'session', values.sessionId ] });
      }}
    >
      <>
        <PsychNotesCPTCodesSection sessionId={sessionId} paperClassName={paperClassName} />
        <FormikAutoSave />
      </>
    </NestedFormik>
  );
};


export type ClinicalNotesSectionType =
  | ClinicalNotesDynamicFormKeyType
  | 'presentingProblems'
  | 'additionalNotes'
  | 'details'
  | 'cptCodes'
  | 'diagnosis'
  | 'treatment'
  | 'identityVerification';

type SectionHashValueType = {
  title: string;
  subtitle?: React.ReactNode;
  wrapContainer?: boolean;
  content: React.ReactNode;
  hidden?: boolean;
};

const ClinicalNotesRaw: React.VFC = () => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const [ savingForLater, setSavingForLater ] = useState(false);
  const { state: { appointment, sessionResource } } = useClinicalNotesContext();
  const { data: clinicalNote } = useSessionClinicalNoteQuery(sessionResource.session.id);
  const { initialValues, onSubmit, onPatch, validateForm } = useClinicalNotesForm();

  const sectionHash: {[key in ClinicalNotesSectionType]: SectionHashValueType} = {
    additionalNotes: {
      title: 'Clinical Summary',
      content: <AdditionalNotes />
    },
    identityVerification: {
      title: l10n.clinicalNotes.identityVerification,
      subtitle: <Typography variant="body2">Insurance companies require that you confirm client identity in every session.</Typography>,
      content: <IdentityVerification />,
    },
    details: {
      title: l10n.clinicalNotes.details,
      content: <Details />
    },
    diagnosis: {
      title: l10n.clinicalNotes.diagnosis,
      wrapContainer: false,
      content: (
        <Box width="100%" maxWidth="1000px">
          <DiagnosisSection
            paperClassName={classes.sectionContainer}
            isDiagnosisReviewRequired={clinicalNote?.notes.isDiagnosisReviewRequired}
          />
        </Box>
      )
    },
    presentingProblems: {
      title: l10n.clinicalNotes.presentingProblems,
      content: <PresentingProblems />
    },
    mentalStatusExam: {
      title: l10n.clinicalNotes.mentalStatusExam,
      content: <MentalStatusExam />
    },
    initialProgress: {
      title: l10n.clinicalNotes.plan,
      content: <Plan />
    },
    followUpProgress: {
      title: l10n.clinicalNotes.progress,
      wrapContainer: false,
      content: (
        <Box className={classes.sectionContainer}>
          <SectionContainer
            title={l10n.clinicalNotes.progress}
            action={clinicalNote?.notes.isFollowUpTreatmentGoalReviewRequired
              && <CheckboxWithLabel showError color="secondary" name="isFollowUpTreatmentGoalReviewed" label="Is reviewed" />
            }
          >
            <Progress />
          </SectionContainer>
        </Box>
      )
      // content: <Progress />
    },
    riskAssessment: {
      title: l10n.clinicalNotes.riskAssessment,
      content: <RiskAssessment />
    },
    treatment: {
      title: l10n.clinicalNotes.treatment,
      wrapContainer: false,
      content: (
        <Box className={classes.sectionContainer}>
          <SectionContainer
            title={l10n.clinicalNotes.treatment}
            action={clinicalNote?.notes.treatment.isTreatmentReviewRequired
              && <CheckboxWithLabel showError color="secondary" name="isTreatmentReviewed" label="Is reviewed" />
            }
          >
            <Treatment />
          </SectionContainer>
        </Box>
      )
    },
    cptCodes: {
      title: l10n.clinicalNotes.cptCodes,
      wrapContainer: false,
      content: (
        <Box width="100%" maxWidth="1000px">
          <ClinicalNotesCPTCodesSection sessionId={sessionResource.session.id} paperClassName={classes.sectionContainer} />
        </Box>
      )
    },
  };

  const templateContentHash: {[key in ClinicalNotesTemplateType]: ClinicalNotesSectionType[]} = {
    initial: [ 'identityVerification', 'details', 'diagnosis', 'presentingProblems', 'additionalNotes', 'mentalStatusExam', 'riskAssessment', 'treatment', 'initialProgress', 'cptCodes' ],
    followUp: [ 'identityVerification', 'details', 'diagnosis', 'presentingProblems', 'additionalNotes', 'mentalStatusExam', 'riskAssessment', 'treatment', 'followUpProgress', 'cptCodes' ]
  };

  return (
    <NestableFormik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={validateForm}
    >
      {(formik) => {
        const template = formik.values.template;
        const showAlert = formik.submitCount > 0 && !formik.isValid;

        return (
          <>
            <ScrollToFieldError />
            <Box
              px={2}
              pt={6}
              pb={10}
              display="flex"
              flexDirection="column"
              gap="32px"
              maxWidth="1000px"
              width="100%"
              margin="0 auto"
            >
              <Box className={classes.sectionContainer}>
                <ClinicalNotesHeader mb={4} />
              </Box>

              {showAlert && (
                <Box mb={5} className={classes.sectionContainer}>
                  <Alert severity="error">
                    <Typography variant="body2" className={classes.alert}>
                      {l10n.clinicalNotes.correctTheErrorsBelow}
                    </Typography>
                  </Alert>
                </Box>
              )}

              <Box className={classes.sectionContainer}>
                <UserSessionInfoCard
                  appointment={{
                    ...appointment,
                    patientId: appointment.patient.id,
                    providerId: appointment.provider.id,
                  }}
                  showActions={false}
                />
              </Box>

              <FormikAutoSave
                onSave={async () => {
                  await onPatch(formik.values, { rerouteOnSuccess: false });
                }}
              />

              {templateContentHash[template].map((sectionKey) => {
                const { title, subtitle, wrapContainer, content, hidden } = sectionHash[sectionKey];

                if (hidden) {
                  return null;
                }

                if (wrapContainer === false) {
                  return <>{content} </>;
                }

                return (
                  <Box className={classes.sectionContainer} key={title}>
                    <SectionContainer title={title} subtitle={subtitle}>{content}</SectionContainer>
                  </Box>
                );
              })}
            </Box>

            <StickyStackItem placement="bottom" order={0}>
              <FixedActionBar
                onSave={() => {
                  formik.handleSubmit();
                }}
                saving={formik.isSubmitting}
                onSaveForLater={async () => {
                  setSavingForLater(true);
                  await onPatch(formik.values, {});
                  setSavingForLater(false);
                }}
                savingForLater={savingForLater}
              />
            </StickyStackItem>
          </>
        );
      }}
    </NestableFormik>
  );
};

const useStyles = makeStyles()((theme) => ({
  sectionContainer: {
    maxWidth: theme.spacing(81), // 648px
    width: '100%',
  },
  alert: {
    // without this, text isn't aligned to icon
    marginTop: theme.spacing(0.2)
  }
}));

export const ClinicalNotes: React.VFC = () => {
  const { state: { appointment } } = useClinicalNotesContext();

  if (appointment.state !== SESSION_STATES.completed) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  return <ClinicalNotesRaw />;
};
